import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary, {
  accordionSummaryClasses,
} from "@mui/material/AccordionSummary";
import { useAppSettings } from "../../context/AppSettingsContext";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { API_ENDPOINTS } from "../../config/api";
import MainLogo from "../../assets/images/steelbudy-logo.svg";

// Custom event name for application settings type updates
const APP_SETTINGS_TYPE_UPDATED_EVENT = 'app_settings_type_updated';

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
  {
    transform: "rotate(90deg)",
  },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
  ...theme.applyStyles("dark", {
    backgroundColor: "rgba(255, 255, 255, .05)",
  }),
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

const LeftSidebarMenu = ({ toggleActive }) => {
  const [expanded, setExpanded] = useState("panel1");
  const location = useLocation(); // Get current location/pathname
  const { user, isAuthenticated, logout } = useAuth();
  const [applicationSettingTypes, setApplicationSettingTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const { appLogo } = useAppSettings();

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const hasProductPermissions = user && user.permissions ? user.permissions.some((perm) =>
    ["view-products", "add-products", "edit-products", "delete-products"].includes(perm)
  ) : false;

  const hasRegionPermissions = user && user.permissions ? user.permissions.some((perm) =>
    ["view-regions", "add-regions", "edit-regions", "delete-regions"].includes(perm)
  ) : false;

  const hasUserTypePermissions = user && user.permissions ? user.permissions.some((perm) =>
    ["view-userTypes", "add-userTypes", "edit-userTypes", "delete-userTypes"].includes(perm)
  ) : false;

  const hasPaymentTermPermissions = user && user.permissions ? user.permissions.some((perm) =>
    ["view-paymentTerms", "add-paymentTerms", "edit-paymentTerms", "delete-paymentTerms"].includes(perm)
  ) : false;

  const hasDeliveryTermsPermissions = user && user.permissions ? user.permissions.some((perm) =>
    ["view-deliveryTerms", "add-deliveryTerms", "edit-deliveryTerms", "delete-deliveryTerms"].includes(perm)
  ) : false;

  const hasProductTypePermissions = user && user.permissions ? user.permissions.some((perm) =>
    ["view-productTypes", "add-productTypes", "edit-productTypes", "delete-productTypes"].includes(perm)
  ) : false;

  const hasCategoryPermissions = user && user.permissions ? user.permissions.some((perm) =>
    ["view-categories", "add-categories", "edit-categories", "delete-categories"].includes(perm)
  ) : false;

  const hasBrandPermissions = user && user.permissions ? user.permissions.some((perm) =>
    ["view-brands", "add-brands", "edit-brands", "delete-brands"].includes(perm)
  ) : false;

  const hasAppUserPermissions = user && user.permissions ? user.permissions.some((perm) =>
    ["view-appUsers", "add-appUsers", "edit-appUsers", "delete-appUsers"].includes(perm)
  ) : false;

  const hasDealerQuotationsPermissions = user && user.permissions ? user.permissions.some((perm) =>
    ["view-dealerQuotations", "add-dealerQuotations", "edit-dealerQuotations", "delete-dealerQuotations"].includes(perm)
  ) : false;

  const hasCustomerOrderPermissions = user && user.permissions ? user.permissions.some((perm) =>
    ["view-customerOrders", "add-customerOrders", "edit-customerOrders", "delete-customerOrders"].includes(perm)
  ) : false;

  const hasCustomOrderProductsPermissions = user && user.permissions ? user.permissions.some((perm) =>
    ["view-customOrderProducts", "add-customOrderProducts", "edit-customOrderProducts", "delete-customOrderProducts"].includes(perm)
  ) : false;

  const hasRolePermissions = user && user.permissions ? user.permissions.some((perm) =>
    ["view-roles", "add-roles", "edit-roles", "delete-roles"].includes(perm)
  ) : false;

  const hasPermissionPermissions = user && user.permissions ? user.permissions.some((perm) =>
    ["view-permissions", "add-permissions", "edit-permissions", "delete-permissions"].includes(perm)
  ) : false;

  const hasUsersPermissions = user && user.permissions ? user.permissions.some((perm) =>
    ["view-users", "add-users", "edit-users", "delete-users"].includes(perm)
  ) : false;

  const hasApplicationTypesPermissions = user && user.permissions ? user.permissions.some((perm) =>
    ["view-applicationTypes", "add-applicationTypes", "edit-applicationTypes", "delete-applicationTypes"].includes(perm)
  ) : false;

  const hasApplicationSettingsPermissions = user && user.permissions ? user.permissions.some((perm) =>
    ["view-applicationSettings", "add-applicationSettings", "edit-applicationSettings", "delete-applicationSettings"].includes(perm)
  ) : false;

  const isActiveLink = (path) => (location.pathname === path ? "active" : "");

  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    if (location.pathname === "/dashboard/beauty-salon/") {
      const storedTheme = localStorage.getItem("beautySalonSidebarTheme");
      if (storedTheme) {
        setIsDark(storedTheme === "dark-theme");
      } else {
        setIsDark(true);
        localStorage.setItem("beautySalonSidebarTheme", "dark-theme");
      }
    } else {
      setIsDark(false);
    }
  }, [location.pathname]);

  const fetchApplicationSettingTypes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (token) {
        const response = await axios.get(API_ENDPOINTS.APPLICATION_TYPES.BASE, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data.success) {
          setApplicationSettingTypes(response.data.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching application setting types:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplicationSettingTypes();

    const intervalId = setInterval(fetchApplicationSettingTypes, 10000);

    return () => clearInterval(intervalId);
  }, [location.pathname]);

  useEffect(() => {
    const handleTypeUpdate = () => {
      console.log('Application settings type updated, refreshing...');
      fetchApplicationSettingTypes();
    };

    window.addEventListener(APP_SETTINGS_TYPE_UPDATED_EVENT, handleTypeUpdate);

    return () => {
      window.removeEventListener(APP_SETTINGS_TYPE_UPDATED_EVENT, handleTypeUpdate);
    };
  }, []);

  useEffect(() => {
    // Remove all relevant classes first
    document.body.classList.remove("customer-orders-tab", "main-theme-settings");
  
    // Only add a class if we're on the theme-settings page
    if (location.pathname === "/theme-settings") {
      // Check if we came from the customer orders link
      if (location.state?.fromCustomerOrders) {
        document.body.classList.add("customer-orders-tab");
      } else {
        document.body.classList.add("main-theme-settings");
      }
    }
  }, [location.pathname, location.state]);
  return (
    <Box
      className={`leftSidebarDark hide-for-horizontal-nav ${location.pathname === "/dashboard/beauty-salon/" && isDark
        ? "dark-theme"
        : ""
        }`}
    >
      <Box className="left-sidebar-menu">
        <Box className="logo">
          <Link to="/dashboard">
            {appLogo ? (
              <img
                src={appLogo}
                alt="logo-icon"
                width={150}
                height={75}
              />
            ) : (
              <img
                src={MainLogo}
                alt="logo-icon"
                width={150}
                height={75}
              />
            )}
          </Link>
        </Box>

        <Box className="burger-menu" onClick={toggleActive}>
          <Typography component={"span"} className="top-bar"></Typography>
          <Typography component={"span"} className="middle-bar"></Typography>
          <Typography component={"span"} className="bottom-bar"></Typography>
        </Box>

        <Box className="sidebar-inner">
          <Box className="sidebar-menu"> 
            <Link to="/dashboard/" className="sidebar-menu-link">
              <span class="material-symbols-outlined">
                dashboard
              </span>               Dashboard
            </Link>
            {hasAppUserPermissions && (
              <Link to="/app-users" className="sidebar-menu-link">
                <span class="material-symbols-outlined">
                  group
                </span> Users List
              </Link>
            )}
            {hasProductPermissions && (
              <Accordion
                expanded={expanded === 'panel29'}
                onChange={handleChange('panel29')}
                className="mat-accordion"
              >
                <AccordionSummary
                  className="mat-summary"
                  aria-controls="panel29d-content"
                  id="panel29d-header"
                >
                  <i className="material-symbols-outlined">shopping_cart</i>
                  <Typography component="span" className="title">
                    Products
                  </Typography>
                </AccordionSummary>

                <AccordionDetails className="mat-details">
                  <ul className="sidebar-sub-menu">
                    <li className="sidemenu-item">
                      <Link
                        to="/products-list"
                        className={`sidemenu-link ${isActiveLink('/products-list')}`}
                      >
                        Products List
                      </Link>
                    </li>
                    {hasCategoryPermissions && (
                      <li className="sidemenu-item">
                        <Link
                          to="/categories"
                          className={`sidemenu-link ${isActiveLink('/categories')}`}
                        >
                          Products Categories
                        </Link>
                      </li>
                    )}
                    {hasProductTypePermissions && (
                      <li className="sidemenu-item">
                        <Link
                          to="/product-types"
                          className={`sidemenu-link ${isActiveLink('/product-types')}`}
                        >
                          Product Types
                        </Link>
                      </li>
                    )}
                  </ul>
                </AccordionDetails>
              </Accordion>
            )}
            {hasCustomerOrderPermissions && (
              <Link to="/customer-orders" className="sidebar-menu-link">
                <span className="material-symbols-outlined">
                  person_add
                </span>
                Enquiries
              </Link>
            )}
            {
              hasDealerQuotationsPermissions && (
                <Link to="/dealer-quotations" className="sidebar-menu-link">
                  <span class="material-symbols-outlined">
                    docs
                  </span>      Quotations
                </Link>
              )
            }
            <Accordion
              expanded={expanded === 'panel32'}
              onChange={handleChange('panel32')}
              className="mat-accordion"
            >
              <AccordionSummary
                className="mat-summary"
                aria-controls="panel32d-content"
                id="panel32d-header"
              >
                <i className="material-symbols-outlined" aria-label="Dynamic Dropdown">settings</i>
                <Typography component="span" className="title">
                  Dynamic Dropdown
                </Typography>
              </AccordionSummary>
              <AccordionDetails className="mat-details">
                <ul className="sidebar-sub-menu">
                  {hasBrandPermissions && (
                    <li className="sidemenu-item">
                      <Link
                        to="/brands"
                        className={`sidemenu-link ${isActiveLink('/brands')}`}
                      >
                        Brands
                      </Link>
                    </li>
                  )}
                  {hasRegionPermissions && (
                    <li className="sidemenu-item">
                      <Link
                        to="/regions"
                        className={`sidemenu-link ${isActiveLink('/regions')}`}
                      >
                        Regions
                      </Link>
                    </li>
                  )}
                  {hasUserTypePermissions && (
                    <li className="sidemenu-item">
                      <Link
                        to="/user-types"
                        className={`sidemenu-link ${isActiveLink('/user-types')}`}
                      >
                        User Types
                      </Link>
                    </li>
                  )}
                  {hasPaymentTermPermissions && (
                    <li className="sidemenu-item">
                      <Link
                        to="/payment-terms"
                        className={`sidemenu-link ${isActiveLink('/payment-terms')}`}
                      >
                        Payment Terms
                      </Link>
                    </li>
                  )}
                  {hasDeliveryTermsPermissions && (
                    <li className="sidemenu-item">
                      <Link
                        to="/delivery-terms"
                        className={`sidemenu-link ${isActiveLink('/delivery-terms')}`}
                      >
                        Delivery Terms
                      </Link>
                    </li>
                  )}
                  {hasDeliveryTermsPermissions && (
                    <li className="sidemenu-item">
                   <Link
  to="/theme-settings"
  state={{ fromCustomerOrders: true }}
  className="sidemenu-link"
>
  Customer Orders Text
</Link>
                  </li>
                  )}
                </ul>
              </AccordionDetails>
            </Accordion>
            {(hasApplicationTypesPermissions || hasApplicationSettingsPermissions) && (
              <Accordion
                expanded={expanded === 'panel31'}
                onChange={handleChange('panel31')}
                className="mat-accordion"
              >
                <AccordionSummary
                  className="mat-summary"
                  aria-controls="panel31d-content"
                  id="panel31d-header"
                >
                  <i className="material-symbols-outlined" aria-label="Application Settings">tune</i>
                  <Typography component="span" className="title">
                    Application Settings
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className="mat-details">
                  <ul className="sidebar-sub-menu">
                    {hasApplicationTypesPermissions && (
                      <li className="sidemenu-item">
                        <Link
                          to="/application-setting-types"
                          className={`sidemenu-link ${isActiveLink('/application-setting-types')}`}
                        >
                          Application Type
                        </Link>
                      </li>
                    )}
                    {hasApplicationSettingsPermissions && (
                      <li className="sidemenu-item">
                        <Link
                          to="/application-settings"
                          className={`sidemenu-link ${isActiveLink('/application-settings')}`}
                        >
                          Application Settings
                        </Link>
                      </li>
                    )}
                  </ul>
                </AccordionDetails>
              </Accordion>
            )}
            {(hasPermissionPermissions || hasRolePermissions || hasUsersPermissions) && (
              <Accordion
                expanded={expanded === 'panel30'}
                onChange={handleChange('panel30')}
                className="mat-accordion"
              >
                <AccordionSummary
                  className="mat-summary"
                  aria-controls="panel30d-content"
                  id="panel30d-header"
                >
                  <i className="material-symbols-outlined" aria-label="User Management">manage_accounts</i>
                  <Typography component="span" className="title">
                    User Management
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className="mat-details">
                  <ul className="sidebar-sub-menu">
                    {hasPermissionPermissions && (
                      <li className="sidemenu-item">
                        <Link
                          to="/permissions"
                          className={`sidemenu-link ${isActiveLink('/permissions')}`}
                        >
                          Permissions List
                        </Link>
                      </li>
                    )}
                    {hasRolePermissions && (
                      <li className="sidemenu-item">
                        <Link
                          to="/roles"
                          className={`sidemenu-link ${isActiveLink('/roles')}`}
                        >
                          Roles
                        </Link>
                      </li>
                    )}
                    {hasUsersPermissions && (
                      <li className="sidemenu-item">
                        <Link
                          to="/users"
                          className={`sidemenu-link ${isActiveLink('/users')}`}
                        >
                          Users List
                        </Link>
                      </li>
                    )}
                  </ul>
                </AccordionDetails>
              </Accordion>
            )}
          <Link 
  to="/theme-settings" 
  state={{ fromCustomerOrders: false }}
  className="sidebar-menu-link"
>
  <span className="material-symbols-outlined">settings</span>
  Theme Settings
</Link>
            <Accordion
              expanded={expanded === 'panel33'}
              onChange={handleChange('panel33')}
              className="mat-accordion"
            >
              <AccordionSummary
                className="mat-summary"
                aria-controls="panel33d-content"
                id="panel33d-header"
              >
                <span class="material-symbols-outlined" style={{ position: 'relative', top: '2px' }}>
                  assignment
                </span>
                <Typography component="span" className="title">
                  &nbsp;  Reports
                </Typography>
              </AccordionSummary>
              <AccordionDetails className="mat-details">
                <ul className="sidebar-sub-menu">
                  <li className="sidemenu-item">
                    <Link
                      to="/app-user-report"
                      className={`sidemenu-link ${isActiveLink('/app-user-report')}`}
                    >
                      App Users
                    </Link>
                  </li>
                  <li className="sidemenu-item">
                    <Link
                      to="/customer-order-report"
                      className={`sidemenu-link ${isActiveLink('/customer-order-report')}`}
                    >
                      Enquiry
                    </Link>
                  </li>
                  <li className="sidemenu-item">
                    <Link
                      to="/dealer-quotation-report"
                      className={`sidemenu-link ${isActiveLink('/dealer-quotation-report')}`}
                    >
                      Quotations
                    </Link>
                  </li>
                </ul>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LeftSidebarMenu;